import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { format, addYears, subYears, parse, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Input } from '@/components/ui/input';
import './advanced-calendar.css';

const VIEW_MODES = {
  DAYS: 'days',
  MONTHS: 'months',
  YEARS: 'years'
};

function AdvancedCalendar({
  className,
  classNames,
  showOutsideDays = true,
  mode = 'single',
  selected,
  onSelect,
  disabled,
  initialFocus,
  ...props
}) {
  const [viewMode, setViewMode] = useState(VIEW_MODES.DAYS);
  const [currentDate, setCurrentDate] = useState(selected || new Date());
  const [manualDateInput, setManualDateInput] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const yearsContainerRef = useRef(null);

  const handleMonthClick = useCallback(() => {
    setViewMode(VIEW_MODES.MONTHS);
  }, []);

  const handleYearClick = useCallback(() => {
    setViewMode(VIEW_MODES.YEARS);
  }, []);

  const handleMonthSelect = useCallback((month) => {
    setCurrentDate(month);
    setViewMode(VIEW_MODES.DAYS);
  }, []);

  const handleYearSelect = useCallback((year) => {
    setCurrentDate(year);
    setViewMode(VIEW_MODES.MONTHS);
  }, []);

  const handleDateSelect = useCallback((date) => {
    if (onSelect) {
      onSelect(date);
    }
    setViewMode(VIEW_MODES.DAYS);
  }, [onSelect]);

  const generateYearRange = (centerYear) => {
    const startYear = centerYear - 15;
    const endYear = centerYear + 15;
    const years = [];
    
    for (let year = startYear; year <= endYear; year++) {
      years.push(new Date(year, 0, 1));
    }
    
    return years;
  };

  const handleManualDateSubmit = useCallback((e) => {
    e.preventDefault();
    
    // Tentar diferentes formatos de data
    const formats = ['dd/MM/yyyy', 'dd-MM-yyyy', 'dd.MM.yyyy', 'yyyy-MM-dd'];
    let parsedDate = null;
    
    for (const formatStr of formats) {
      try {
        const date = parse(manualDateInput, formatStr, new Date());
        if (isValid(date)) {
          parsedDate = date;
          break;
        }
      } catch (error) {
        continue;
      }
    }
    
    if (parsedDate) {
      setCurrentDate(parsedDate);
      if (onSelect) {
        onSelect(parsedDate);
      }
      setViewMode(VIEW_MODES.DAYS);
      setManualDateInput('');
      setShowManualInput(false);
    } else {
      alert('Data inválida. Use o formato DD/MM/AAAA');
    }
  }, [manualDateInput, onSelect]);

  const navigateYears = useCallback((direction) => {
    if (viewMode === VIEW_MODES.YEARS) {
      // Navegar por décadas (30 anos por vez)
      const newYear = direction === 'next' 
        ? currentDate.getFullYear() + 30 
        : currentDate.getFullYear() - 30;
      setCurrentDate(new Date(newYear, 0, 1));
    } else if (viewMode === VIEW_MODES.MONTHS) {
      // Navegar por anos
      const newYear = direction === 'next' 
        ? currentDate.getFullYear() + 1 
        : currentDate.getFullYear() - 1;
      setCurrentDate(new Date(newYear, 0, 1));
    }
  }, [viewMode, currentDate]);

  // Scroll automático para o ano atual quando abrir a visualização de anos
  useEffect(() => {
    if (viewMode === VIEW_MODES.YEARS && yearsContainerRef.current) {
      const currentYear = new Date().getFullYear();
      const years = generateYearRange(currentDate.getFullYear());
      const currentYearIndex = years.findIndex(year => year.getFullYear() === currentYear);
      
      if (currentYearIndex !== -1) {
        const rowHeight = 64; // altura de cada linha de anos
        const rowIndex = Math.floor(currentYearIndex / 3);
        const scrollTop = rowIndex * rowHeight;
        
        yearsContainerRef.current.scrollTop = scrollTop;
      }
    }
  }, [viewMode, currentDate]);

  const renderDaysView = () => (
    <div className="space-y-3">
      {/* Campo de entrada manual */}
      <div className="flex items-center gap-2">
        <Input
          type="text"
          placeholder="DD/MM/AAAA"
          value={manualDateInput}
          onChange={(e) => setManualDateInput(e.target.value)}
          className="flex-1 text-sm"
          onKeyPress={(e) => e.key === 'Enter' && handleManualDateSubmit(e)}
        />
        <button
          type="button"
          onClick={handleManualDateSubmit}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'px-3')}
        >
          OK
        </button>
      </div>
      
      <DayPicker
        mode={mode}
        selected={selected}
        onSelect={handleDateSelect}
        disabled={disabled}
        showOutsideDays={showOutsideDays}
        month={currentDate}
        onMonthChange={setCurrentDate}
        className={cn('p-3', className)}
        locale={ptBR}
        classNames={{
          months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          caption: 'flex justify-center pt-1 relative items-center',
          caption_label: 'text-sm font-medium cursor-pointer hover:text-primary transition-colors',
          nav: 'space-x-1 flex items-center',
          nav_button: cn(buttonVariants({ variant: 'outline' }), 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'),
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day: cn(buttonVariants({ variant: 'ghost' }), 'h-9 w-9 p-0 font-normal aria-selected:opacity-100'),
          day_selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
          day_today: 'bg-accent text-accent-foreground',
          day_outside: 'text-muted-foreground opacity-50',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames
        }}
        components={{
          IconLeft: ({ ...props }) => <ChevronLeft className="h-4 w-4" />,
          IconRight: ({ ...props }) => <ChevronRight className="h-4 w-4" />,
          CaptionLabel: ({ displayMonth, ...props }) => (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleMonthClick}
                className="hover:text-primary transition-colors"
              >
                {format(displayMonth, 'MMMM', { locale: ptBR })}
              </button>
              <button
                type="button"
                onClick={handleYearClick}
                className="hover:text-primary transition-colors"
              >
                {format(displayMonth, 'yyyy')}
              </button>
            </div>
          )
        }}
        {...props}
      />
    </div>
  );

  const renderMonthsView = () => (
    <div className="p-3">
      <div className="flex justify-center items-center mb-4">
        <button
          type="button"
          onClick={() => navigateYears('prev')}
          className={cn(buttonVariants({ variant: 'outline' }), 'h-8 w-8 p-0')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="mx-4 text-lg font-medium">
          {format(currentDate, 'yyyy')}
        </span>
        <button
          type="button"
          onClick={() => navigateYears('next')}
          className={cn(buttonVariants({ variant: 'outline' }), 'h-8 w-8 p-0')}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 12 }, (_, i) => {
          const month = new Date(currentDate.getFullYear(), i, 1);
          return (
            <button
              key={i}
              type="button"
              onClick={() => handleMonthSelect(month)}
              className={cn(
                buttonVariants({ variant: 'outline' }),
                'h-16 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors',
                currentDate.getMonth() === i && 'bg-primary text-primary-foreground'
              )}
            >
              {format(month, 'MMM', { locale: ptBR })}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderYearsView = () => {
    const years = generateYearRange(currentDate.getFullYear());
    const currentYear = new Date().getFullYear();
    
    return (
      <div className="p-3">
        <div className="flex justify-center items-center mb-4">
          <button
            type="button"
            onClick={() => navigateYears('prev')}
            className={cn(buttonVariants({ variant: 'outline' }), 'h-8 w-8 p-0')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="mx-4 text-lg font-medium">
            {format(years[0], 'yyyy')} - {format(years[years.length - 1], 'yyyy')} ({years.length} anos)
          </span>
          <button
            type="button"
            onClick={() => navigateYears('next')}
            className={cn(buttonVariants({ variant: 'outline' }), 'h-8 w-8 p-0')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        
        {/* Container com scroll para os anos */}
        <div 
          ref={yearsContainerRef}
          className="years-scroll-container advanced-calendar-scroll scroll-indicator"
        >
          <div className="grid grid-cols-3 gap-2 pb-2">
            {years.map((year) => (
              <button
                key={year.getFullYear()}
                type="button"
                onClick={() => handleYearSelect(year)}
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'h-16 text-sm font-medium hover:bg-primary hover:text-primary-foreground year-button',
                  year.getFullYear() === currentYear && 'bg-primary text-primary-foreground current-year'
                )}
              >
                {format(year, 'yyyy')}
              </button>
            ))}
          </div>
        </div>
        
        {/* Indicador de scroll */}
        <div className="text-xs text-center text-muted-foreground mt-2 flex items-center justify-center gap-2">
          <span>📱</span>
          <span>Use o scroll do mouse ou as setas para navegar</span>
          <span>📱</span>
        </div>
      </div>
    );
  };

  const renderView = () => {
    switch (viewMode) {
      case VIEW_MODES.MONTHS:
        return renderMonthsView();
      case VIEW_MODES.YEARS:
        return renderYearsView();
      default:
        return renderDaysView();
    }
  };

  return (
    <div className="relative">
      {renderView()}
    </div>
  );
}

AdvancedCalendar.displayName = 'AdvancedCalendar';

export { AdvancedCalendar };
